import React, { useEffect, useMemo, useRef, useState } from 'react'
import { CrosshairMode } from 'lightweight-charts'
import PropTypes from 'prop-types'

/**
 * hotcurves Chart component.
 *
 * Responsibilities:
 * - Loads Lightweight Charts (LW) from local ESM bundle if available, or falls back to CDN-based standalone build if not.
 * - Supports both line and candlestick series, including cross-version compatibility (v3–v5 and vendor builds).
 * - Handles data aggregation, deduplication, and timeframe bucketing.
 * - Provides a highly interactive chart with overlays, tooltips, and mini-holder trends.
 * - Includes MC/Price toggle, timeframe switching, dynamic axis formatting, and dev event markers.
 * - Ensures main thread responsiveness and robust error handling for all charting operations.
 *
 * Notes:
 * - Chart lifecycle is managed to ensure only a single instance exists at a time.
 * - Series creation, data updates, and axis formatting are cross-version compatible.
 * - Overlay controls and mini-holder chart are optional and controlled via props.
 *
 * Props:
 * @param {Array} data - Main chart data (candles or line points).
 * @param {number} ethPrice - ETH price for reference.
 * @param {string} valueKey - Optional key for extracting value from data objects.
 * @param {boolean} forceLine - Force line mode even if OHLC fields are present.
 * @param {function} onChartReady - Callback when chart is mounted/unmounted.
 * @param {boolean} overlayControls - Show overlay controls (timeframe, toggle, legend).
 * @param {'mcap'|'price'} mode - Current chart mode (market cap or price).
 * @param {function} onModeChange - Callback for changing mode.
 * @param {number|string} height - Chart height.
 * @param {boolean} autoFitOnFirstData - Auto-fit chart on first data load.
 * @param {Array} holdersTrend - Data for the holders mini chart.
 * @param {boolean} showHoldersMini - Show the holders mini chart.
 * @param {Array} devEvents - Developer event markers (buy/sell).
 */

// CDN loader: Loads the official standalone Lightweight Charts bundle from unpkg if the local build is missing or only exposes private APIs.
const loadStandaloneLW = () =>
  new Promise((resolve, reject) => {
    try {
      if (window?.LightweightCharts?.createChart)
        return resolve(window.LightweightCharts)
    } catch (_) {}
    const id = 'lw-standalone-fallback'
    const existing = document.getElementById(id)
    if (existing) {
      existing.addEventListener(
        'load',
        () => resolve(window.LightweightCharts),
        { once: true }
      )
      existing.addEventListener('error', reject, { once: true })
      return
    }
    const s = document.createElement('script')
    s.id = id
    s.async = true
    s.src =
      'https://unpkg.com/lightweight-charts@4.2.0/dist/lightweight-charts.standalone.production.js'
    s.onload = () => resolve(window.LightweightCharts)
    s.onerror = reject
    document.head.appendChild(s)
  })

// Local loader: Attempts to import the local lightweight-charts ESM bundle (preferred, no network). Falls back to null if not available or lacks createChart.
const loadLocalLW = async () => {
  try {
    const mod = await import('lightweight-charts')
    if (mod?.createChart) return mod
  } catch (_) {}
  return null
}

// Loader race: Start both local and CDN loads in parallel. Whichever resolves first with a working createChart API is used. If both fail quickly, try local once more as a fallback.
const raceLoadLW = () => {
  const localP = loadLocalLW().then(m =>
    m && m.createChart
      ? { src: 'local', mod: m }
      : Promise.reject(new Error('no local createChart'))
  )
  const cdnP = loadStandaloneLW().then(m =>
    m && m.createChart
      ? { src: 'cdn', mod: m }
      : Promise.reject(new Error('no cdn createChart'))
  )
  // Prefer whichever resolves first; if both reject fast, try local once more without guard
  return Promise.race([localP, cdnP]).catch(async () => {
    try {
      const m = await import('lightweight-charts')
      if (m?.createChart) return { src: 'local', mod: m }
    } catch (_) {}
    return null
  })
}

// Vendor wrapper unwrapping: Some vendor builds or wrappers may return chart-like objects with the API nested under .chart, .api, _chart, or _internal_chart. This function recursively unwraps to find the core chart instance.
const unwrapChart = chartLike => {
  if (!chartLike) return null
  if (typeof chartLike.addLineSeries === 'function') return chartLike
  if (chartLike.chart && typeof chartLike.chart.addLineSeries === 'function')
    return chartLike.chart
  if (chartLike.api && typeof chartLike.api.addLineSeries === 'function')
    return chartLike.api
  if (chartLike._chart && typeof chartLike._chart.addLineSeries === 'function')
    return chartLike._chart
  if (
    chartLike._internal_chart &&
    typeof chartLike._internal_chart.addLineSeries === 'function'
  )
    return chartLike._internal_chart
  return chartLike
}

const hasAnySeriesApi = c =>
  !!c &&
  (typeof c.addLineSeries === 'function' ||
    typeof c.addAreaSeries === 'function' ||
    typeof c.addBaselineSeries === 'function' ||
    typeof c.addHistogramSeries === 'function' ||
    typeof c.addCandlestickSeries === 'function' ||
    typeof c.addSeries === 'function')

// Cross-version series creation (line-like): Tries to create a line series using all known APIs across LW v3–v5 and vendor forks. Falls back to area, baseline, histogram, or generic addSeries signatures.
const addLineSeriesCompat = (chartLike, options = {}) => {
  const chart = unwrapChart(chartLike)

  // Standard API (v3–v5)
  if (chart && typeof chart.addLineSeries === 'function')
    return chart.addLineSeries(options)

  // Some vendor builds expose area/baseline only; use a line-like series so we at least render
  if (chart && typeof chart.addAreaSeries === 'function')
    return chart.addAreaSeries({ lineWidth: 2, ...options })
  if (chart && typeof chart.addBaselineSeries === 'function')
    return chart.addBaselineSeries({
      baseValue: { type: 'price', price: 0 },
      ...options
    })

  // Experimental addSeries signatures observed in a few forks
  try {
    if (chart && typeof chart.addSeries === 'function')
      return chart.addSeries('Line', options)
  } catch (_) {}
  try {
    if (chart && typeof chart.addSeries === 'function')
      return chart.addSeries({ type: 'Line', ...options })
  } catch (_) {}
  try {
    if (chart && typeof chart.addSeries === 'function')
      return chart.addSeries('line', options)
  } catch (_) {}
  try {
    if (chart && typeof chart.addSeries === 'function')
      return chart.addSeries({ type: 'line', ...options })
  } catch (_) {}

  // Final: create any series available so the app doesn't crash
  if (chart && typeof chart.addHistogramSeries === 'function')
    return chart.addHistogramSeries(options)

  // If we still don't see any series API, give up explicitly
  if (!hasAnySeriesApi(chart)) return null

  return null
}

// Cross-version series creation (candles): Tries to create a candlestick series using all known LW and vendor signatures. Throws if none found.
const addCandleSeriesCompat = (chart, options = {}) => {
  if (typeof chart.addCandlestickSeries === 'function')
    return chart.addCandlestickSeries(options)
  try {
    return chart.addSeries('Candlestick', options)
  } catch (_) {}
  try {
    return chart.addSeries({ type: 'Candlestick', ...options })
  } catch (_) {}
  try {
    return chart.addSeries('candlestick', options)
  } catch (_) {}
  try {
    return chart.addSeries({ type: 'candlestick', ...options })
  } catch (_) {}
  throw new Error(
    'No compatible addCandlestickSeries found for this LW version'
  )
}

// OHLC detection: Determines if the provided array of data objects contains open/close (or full OHLC) fields, indicating candlestick compatibility.
const hasOHLC = arr => {
  if (!Array.isArray(arr) || arr.length === 0) return false
  const d = arr[0]
  const hasOpenClose =
    d && typeof d === 'object' && d.open != null && d.close != null
  const hasFull = hasOpenClose && d.high != null && d.low != null
  return hasOpenClose || hasFull
}

const toNum = v => {
  try {
    const n = typeof v === 'bigint' ? Number(v) : Number(v)
    return Number.isFinite(n) ? n : 0
  } catch {
    return 0
  }
}

const toUnix = (t, i) => {
  const n = Number(t)
  if (!Number.isFinite(n)) return i + 1
  return n > 1e12 ? Math.floor(n / 1000) : Math.floor(n) // ms → s
}

const pickTime = (d, i) => toUnix(d?.time ?? d?.timestamp ?? i + 1, i)

// Price-like extraction: Extracts the numeric price value for a data point, preferring canonical fields (priceUsd, close, value, etc). Ignores amount/volume fields.
const pickNumericValue = (d, opts = {}) => {
  if (d == null) return null
  if (typeof d !== 'object') return toNum(d)

  if ('value' in d && ('time' in d || 'timestamp' in d)) {
    const n = toNum(d.value)
    return Number.isFinite(n) ? n : null
  }

  const { valueKey } = opts
  if (valueKey && d[valueKey] != null) {
    const n = toNum(d[valueKey])
    return Number.isFinite(n) ? n : null
  }

  const cand =
    d.priceUsd ?? d.priceUSD ?? d.usd ?? d.price ?? d.close ?? d.c ?? null
  const n = toNum(cand)
  return Number.isFinite(n) ? n : null
}

const dedupeLineSorted = arr => {
  if (!Array.isArray(arr) || arr.length === 0) return []
  const out = []
  let lastTime = -Infinity
  for (let i = 0; i < arr.length; i++) {
    const it = arr[i]
    const t = Number(it?.time)
    if (!Number.isFinite(t)) continue
    if (t > lastTime) {
      out.push(it)
      lastTime = t
    } else if (t === lastTime) {
      out[out.length - 1] = it
    }
  }
  return out
}

const dedupeCandlesSorted = arr => {
  if (!Array.isArray(arr) || arr.length === 0) return []
  const out = []
  let last = null
  for (let i = 0; i < arr.length; i++) {
    const it = arr[i]
    const t = Number(it?.time)
    if (!Number.isFinite(t)) continue
    if (!last || t > last.time) {
      last = { ...it }
      out.push(last)
    } else if (t === last.time) {
      last.high = Math.max(last.high, it.high)
      last.low = Math.min(last.low, it.low)
      last.close = it.close
    }
  }
  return out
}

// Pre-aggregation: Buckets line data by minute (using the mean) to smooth outliers and reduce noise, with O(n) cost.
const bucketToMinute = t => Math.floor(Number(t) / 60) * 60
const mean = arr => {
  if (!arr.length) return null
  let s = 0
  for (let i = 0; i < arr.length; i++) s += arr[i]
  return s / arr.length
}
const aggregateLineByMinute = arr => {
  if (!Array.isArray(arr) || arr.length === 0) return []
  const buckets = new Map()
  for (const it of arr) {
    const t = bucketToMinute(it.time)
    if (!Number.isFinite(t)) continue
    const list = buckets.get(t) ?? []
    list.push(Number(it.value))
    buckets.set(t, list)
  }
  const out = []
  for (const [t, list] of buckets.entries()) {
    const m = mean(list)
    if (m != null && Number.isFinite(m)) out.push({ time: t, value: m })
  }
  out.sort((a, b) => a.time - b.time)
  return out
}

// Timeframe bucketing: Converts a timeframe in minutes to a bucket size in seconds (minimum 60s). Used to aggregate data points for larger timeframes.
const bucketSecFromTf = tfMin => Math.max(60, Number(tfMin) * 60 || 60)

// Bucket OHLC by timeframe: Aggregates an array of OHLC candles into larger timeframe buckets (e.g., 5m, 15m) by open, high, low, close, and volume.
const bucketCandles = (arr, tfMin = 1) => {
  if (!Array.isArray(arr) || arr.length === 0) return []
  const bs = bucketSecFromTf(tfMin)
  const buckets = new Map()
  for (let i = 0; i < arr.length; i++) {
    const p = arr[i]
    const t = Math.floor(Number(p.time) / bs) * bs
    if (!Number.isFinite(t)) continue
    let b = buckets.get(t)
    if (!b) {
      b = {
        time: t,
        open: Number(p.open),
        high: Number(p.high),
        low: Number(p.low),
        close: Number(p.close),
        firstTime: Number(p.time),
        lastTime: Number(p.time),
        volume: Number(p.volume || 0),
        buyVolume: Number(p.buyVolume || 0),
        sellVolume: Number(p.sellVolume || 0)
      }
      buckets.set(t, b)
    } else {
      // open = earliest in bucket, close = latest in bucket
      if (Number(p.time) < b.firstTime) {
        b.open = Number(p.open)
        b.firstTime = Number(p.time)
      }
      if (Number(p.time) > b.lastTime) {
        b.close = Number(p.close)
        b.lastTime = Number(p.time)
      }
      if (Number(p.high) > b.high) b.high = Number(p.high)
      if (Number(p.low) < b.low) b.low = Number(p.low)
      b.volume += Number(p.volume || 0)
      b.buyVolume += Number(p.buyVolume || 0)
      b.sellVolume += Number(p.sellVolume || 0)
    }
  }
  return Array.from(buckets.values()).sort((a, b) => a.time - b.time)
}

// Bucket line series: Aggregates a line series by averaging values per timeframe bucket and summing volumes.
const bucketLine = (arr, tfMin = 1) => {
  if (!Array.isArray(arr) || arr.length === 0) return []
  const bs = bucketSecFromTf(tfMin)
  const buckets = new Map()
  for (let i = 0; i < arr.length; i++) {
    const p = arr[i]
    const t = Math.floor(Number(p.time) / bs) * bs
    if (!Number.isFinite(t)) continue
    let b = buckets.get(t)
    if (!b) {
      b = {
        time: t,
        values: [Number(p.value)],
        volume: Number(p.volume || 0),
        buyVolume: Number(p.buyVolume || 0),
        sellVolume: Number(p.sellVolume || 0)
      }
      buckets.set(t, b)
    } else {
      b.values.push(Number(p.value))
      b.volume += Number(p.volume || 0)
      b.buyVolume += Number(p.buyVolume || 0)
      b.sellVolume += Number(p.sellVolume || 0)
    }
  }
  const out = []
  for (const b of buckets.values()) {
    const vals = b.values.filter(v => Number.isFinite(v))
    if (!vals.length) continue
    const avg = vals.reduce((s, v) => s + v, 0) / vals.length
    out.push({
      time: b.time,
      value: avg,
      volume: b.volume,
      buyVolume: b.buyVolume,
      sellVolume: b.sellVolume
    })
  }
  out.sort((a, b) => a.time - b.time)
  return out
}
// Dynamic precision: Picks the optimal number of decimal places for tiny USD prices so that very small values don't render as $0.00.
const pickPrecision = v => {
  const x = Math.abs(Number(v) || 0)
  if (!Number.isFinite(x) || x === 0) return 2
  if (x < 1e-5) return 8
  if (x < 1e-4) return 7
  if (x < 1e-3) return 6
  if (x < 1e-2) return 5
  if (x < 1e-1) return 4
  return 2
}

// Axis formatter: Formats USD values for the Y-axis. Uses compact notation (K/M/B) for large values, and dynamic precision for tiny prices to ensure visibility.
const axisUsdFormatter = v => {
  const n = Number(v)
  if (!Number.isFinite(n)) return '$0'
  const abs = Math.abs(n)
  if (abs >= 1000) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumFractionDigits: 2
      }).format(n)
    } catch (_) {
      // Fallback
      return '$' + (n / 1000).toFixed(2) + 'K'
    }
  }
  const prec = Math.min(8, Math.max(2, pickPrecision(n)))
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: prec,
      maximumFractionDigits: prec
    }).format(n)
  } catch (_) {
    return '$' + n.toFixed(prec)
  }
}
// Performance guard: Caps the number of points for the initial chart paint to keep the main thread responsive and avoid UI freezes with huge data arrays.
const MAX_POINTS = 4000
const takeLast = (arr, n) =>
  Array.isArray(arr) && arr.length > n ? arr.slice(arr.length - n) : arr

// Reusable USD formatter for Y-axis
const usdPriceFormat = {
  type: 'custom',
  minMove: 0.01,
  formatter: price => {
    const n = Number(price)
    if (!Number.isFinite(n)) return '$0.00'
    return (
      '$' +
      n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    )
  }
}

// Compact USD for axis labels (K/M/B); full value is shown in tooltip
const usdCompactFormat = {
  type: 'custom',
  minMove: 0.01,
  formatter: price => {
    const v = Number(price)
    if (!Number.isFinite(v)) return '$0'
    const abs = Math.abs(v)
    const fmt =
      abs >= 1e9
        ? (v / 1e9).toFixed(2) + 'B'
        : abs >= 1e6
        ? (v / 1e6).toFixed(2) + 'M'
        : abs >= 1e3
        ? (v / 1e3).toFixed(2) + 'K'
        : v.toFixed(2)
    return '$' + fmt
  }
}

// UI color constants for consistent styling
const UI = {
  bg: '#171a21',
  text: '#cfcfcf',
  grid: 'rgba(255,255,255,0.06)',
  bull: '#00ff95',
  bear: '#ff3b81',
  accent: '#b043f5', // HotCurves magenta for highlights
  areaTop: 'rgba(0,255,149,0.24)',
  areaBottom: 'rgba(0,255,149,0.02)'
}

const VOL = {
  up: 'rgba(0, 255, 149, 0.28)',
  down: 'rgba(255, 59, 129, 0.32)',
  neutral: 'rgba(176,176,176,0.25)'
}

const MyChart = ({
  data = [],
  ethPrice = 1,
  valueKey,
  forceLine = false,
  onChartReady,
  overlayControls = false,
  mode = 'mcap', // 'mcap' | 'price'
  onModeChange = () => {},
  height = 500,
  autoFitOnFirstData = true,
  holdersTrend = [],
  showHoldersMini: showHoldersMiniProp = true,
  devEvents = []
}) => {
  // State for toggling holders mini chart
  const [showHoldersMini] = useState(showHoldersMiniProp)

  // Local timeframe in minutes (1, 5, 15, 60, 1440)
  const [timeframe, setTimeframe] = useState(1)

  // Store the latest onChartReady callback in a ref so we can invoke it without
  // re-creating the initialization effect on every render. This avoids the
  // `react-hooks/exhaustive-deps` warning about having a conditional callback
  // in the dependency list of useEffect. See: https://reactjs.org/docs/hooks-faq.html#how-to-read-an-often-changing-value-from-useeffect
  const onChartReadyRef = useRef(onChartReady)
  useEffect(() => {
    onChartReadyRef.current = onChartReady
  }, [onChartReady])

  const seriesRef = useRef(null)
  const chartRef = useRef(null)
  const containerRef = useRef(null)
  const seriesKindRef = useRef('line') // 'candle' | 'line'
  const resizeObserverRef = useRef(null)
  const firstFitDoneRef = useRef(false)
  const firstResizeFitDoneRef = useRef(false)
  const dataSigRef = useRef('')
  // Track a pending requestAnimationFrame id so we can cancel it in cleanup
  const resizeRAFIdRef = useRef(0)
  const priceLineRef = useRef(null)
  const lastPriceRef = useRef(NaN)
  const tooltipRef = useRef(null)
  const unsubscribeRef = useRef(null)
  const volumeSeriesRef = useRef(null)
  const volDataRef = useRef({ arr: [], map: new Map() })
  const volIdxRef = useRef(new Map())
  const priceCacheRef = useRef({ arr: [], idx: new Map() })
  const holdersSeriesRef = useRef(null)
  const holdersIdxRef = useRef(new Map())
  const holdersDataRef = useRef({ arr: [], map: new Map() })
  // Ref for the mini-pane label
  const holdersLabelRef = useRef(null)

  // Separate mini-pane (own chart) for holders trend
  const holdersPaneRef = useRef(null)
  const holdersChartRef = useRef(null)
  const holdersAreaRef = useRef(null)
  const syncingRangeRef = useRef(false)

  // --- helpers to create/destroy the holders mini chart on demand ---
  const createHoldersMini = async () => {
    try {
      const c = chartRef.current
      const hEl = holdersPaneRef.current
      if (!c || !hEl || holdersChartRef.current) return

      const targetH =
        parseFloat(getComputedStyle(hEl).getPropertyValue('--holders-h')) ||
        hEl.clientHeight ||
        0
      if (targetH <= 1) {
        requestAnimationFrame(() => createHoldersMini())
        return
      }

      // Reserve same right axis width as main chart
      try {
        const ps = c.priceScale ? c.priceScale('right') : null
        const w = ps && typeof ps.width === 'function' ? ps.width() : 0
        if (Number.isFinite(w) && w > 0) hEl.style.paddingRight = w + 'px'
      } catch (_) {}

      const hWidth = Math.max(200, hEl.clientWidth || 600)
      const hHeight = Math.max(64, hEl.clientHeight || 96)

      let hc
      try {
        const res = await raceLoadLW()
        if (res?.mod?.createChart) {
          hc = res.mod.createChart(hEl, {
            layout: {
              textColor: UI.text,
              background: { type: 'solid', color: UI.bg }
            },
            grid: {
              vertLines: { color: UI.grid },
              horzLines: { color: UI.grid }
            },
            rightPriceScale: { borderColor: '#3a3a3c' },
            timeScale: {
              borderColor: '#3a3a3c',
              timeVisible: true,
              secondsVisible: true
            },
            width: hWidth,
            height: hHeight
          })
        }
      } catch (_) {}
      if (!hc && window?.LightweightCharts?.createChart) {
        hc = window.LightweightCharts.createChart(hEl, {
          layout: {
            textColor: UI.text,
            background: { type: 'solid', color: UI.bg }
          },
          grid: {
            vertLines: { color: UI.grid },
            horzLines: { color: UI.grid }
          },
          rightPriceScale: { borderColor: '#3a3a3c' },
          timeScale: {
            borderColor: '#3a3a3c',
            timeVisible: true,
            secondsVisible: true
          },
          width: hWidth,
          height: hHeight
        })
      }
      if (!hc) return

      // ensure plot width excludes reserved right padding
      const pr = parseFloat(getComputedStyle(hEl).paddingRight || '0') || 0
      try {
        hc.applyOptions({
          width: Math.max(200, Math.floor(hWidth - pr)),
          height: hHeight
        })
      } catch (_) {}
      holdersChartRef.current = hc
      try {
        hc.applyOptions({ crosshair: { mode: CrosshairMode.Hidden } })
      } catch (_) {}

      // Add an explanatory label on the bottom-left of the holders mini-pane
      try {
        const label = document.createElement('div')
        label.textContent = 'grwoth indicator: cumulative holder'
        label.style.position = 'absolute'
        label.style.right = '60px'
        label.style.bottom = '30px'
        label.style.padding = '2px 6px'
        label.style.borderRadius = '6px'
        label.style.fontSize = 'clamp(9px, 2.5vw, 11px)'
        label.style.fontWeight = '600'
        label.style.letterSpacing = '0.2px'
        label.style.color = '#cfcfcf'
        label.style.background = 'rgba(12,12,14,0.7)'
        label.style.border = '1px solid #3a3a3c'
        label.style.pointerEvents = 'none'
        label.style.zIndex = '3'
        holdersPaneRef.current.style.position = 'relative'
        holdersPaneRef.current.appendChild(label)
        holdersLabelRef.current = label
      } catch (_) {}

      // compact axis formatter
      try {
        hc.applyOptions({
          localization: {
            priceFormatter: val => {
              const n = Number(val)
              if (!Number.isFinite(n)) return ''
              if (n >= 1e6) return (n / 1e6).toFixed(2) + 'M'
              if (n >= 1e3) return (n / 1e3).toFixed(3) + 'K'
              return String(Math.round(n))
            }
          }
        })
      } catch (_) {}

      const ha = hc.addAreaSeries({
        lastValueVisible: false,
        priceLineVisible: false,
        lineWidth: 2,
        lineColor: 'rgba(64, 160, 255, 0.95)',
        topColor: 'rgba(64, 160, 255, 0.18)',
        bottomColor: 'rgba(64, 160, 255, 0.03)',
        priceFormat: {
          type: 'custom',
          minMove: 1,
          formatter: val => {
            const n = Number(val)
            if (!Number.isFinite(n)) return ''
            if (n >= 1e6) return (n / 1e6).toFixed(2) + 'M'
            if (n >= 1e3) return (n / 1e3).toFixed(3) + 'K'
            return String(Math.round(n))
          }
        }
      })
      holdersAreaRef.current = ha

      // seed data
      if (Array.isArray(holdersTrend) && holdersTrend.length) {
        const _h = holdersTrend
          .map(d => ({ time: Number(d.time), value: Number(d.value) }))
          .filter(p => Number.isFinite(p.time) && Number.isFinite(p.value))
          .sort((a, b) => a.time - b.time)
        ha.setData(_h)
        rebuildHoldersCache(_h)
      }

      // keep ranges aligned
      const syncRange = (src, dst) => {
        const r = src.timeScale().getVisibleLogicalRange?.()
        if (!r || syncingRangeRef.current) return
        syncingRangeRef.current = true
        try {
          dst.timeScale().setVisibleLogicalRange?.(r)
        } finally {
          syncingRangeRef.current = false
        }
      }
      syncRange(c, hc)
      c.timeScale().subscribeVisibleLogicalRangeChange?.(() => syncRange(c, hc))
      hc.timeScale().subscribeVisibleLogicalRangeChange?.(() =>
        syncRange(hc, c)
      )

      // final alignment pass
      alignHoldersAxis()
    } catch (_) {}
  }

  const destroyHoldersMini = () => {
    try {
      if (holdersLabelRef.current) {
        holdersLabelRef.current.remove()
      }
    } catch (_) {}
    holdersLabelRef.current = null
    try {
      holdersChartRef.current?.remove?.()
    } catch (_) {}
    holdersChartRef.current = null
    holdersAreaRef.current = null
    // clear cached data so tooltip doesn't show stale holders when hidden
    try {
      rebuildHoldersCache([])
    } catch (_) {}
    // also remove any extra padding we added
    try {
      if (holdersPaneRef.current)
        holdersPaneRef.current.style.paddingRight = '0px'
    } catch (_) {}
  }

  // Ensure holders mini-pane axis stays visible and aligned with main chart
  const alignHoldersAxis = (attempt = 0) => {
    try {
      const c = chartRef.current
      const hc = holdersChartRef.current
      const hEl = holdersPaneRef.current
      if (!c || !hc || !hEl) return

      // Force axes visible (some builds momentarily hide on formatter flips)
      try {
        c.applyOptions({
          rightPriceScale: { visible: true, borderVisible: true }
        })
      } catch (_) {}
      try {
        hc.applyOptions({
          rightPriceScale: { visible: true, borderVisible: true }
        })
      } catch (_) {}

      const doAlign = () => {
        try {
          const mainPs = c.priceScale ? c.priceScale('right') : null
          const holdPs = hc.priceScale ? hc.priceScale('right') : null
          const mainW =
            mainPs && typeof mainPs.width === 'function' ? mainPs.width() : 0
          const holdW =
            holdPs && typeof holdPs.width === 'function' ? holdPs.width() : 0

          // If widths are still zero (layout not settled), retry once or twice
          if ((mainW === 0 || holdW === 0) && attempt < 2) {
            setTimeout(() => alignHoldersAxis(attempt + 1), 80)
            return
          }

          const delta =
            Number.isFinite(mainW) && Number.isFinite(holdW) ? mainW - holdW : 0
          const pr = Math.max(0, delta)
          hEl.style.paddingRight = pr > 0 ? pr + 'px' : '0px'

          const rect = hEl.getBoundingClientRect()
          hc.applyOptions({
            width: Math.max(200, Math.floor(rect.width - pr)),
            height: Math.max(64, Math.floor(rect.height || 96))
          })
        } catch (_) {}
      }

      // Measure after a frame to avoid 0-width axes during reflow
      requestAnimationFrame(doAlign)
    } catch (_) {}
  }

  // Last point snapshot for overlay legend
  const lastPoint = useMemo(
    () =>
      Array.isArray(data) && data.length ? data[data.length - 1] : undefined,
    [data]
  )
  const lastValue = useMemo(() => {
    if (!lastPoint) return undefined
    const v = valueKey
      ? lastPoint?.[valueKey]
      : lastPoint.close ?? lastPoint.value ?? lastPoint.open ?? null
    return Number.isFinite(Number(v)) ? Number(v) : undefined
  }, [lastPoint, valueKey])
  const fmtUSDcompact = v => {
    const n = Number(v)
    if (!Number.isFinite(n)) return '-'
    const abs = Math.abs(n)

    // Use compact currency for thousands and above
    if (abs >= 1000) {
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          notation: 'compact',
          maximumFractionDigits: 2
        }).format(n)
      } catch (_) {
        return '$' + (n / 1000).toFixed(2) + 'K'
      }
    }

    // For sub-1000 values (esp. tiny prices), use dynamic precision up to 8 dp
    const prec = Math.min(8, Math.max(2, pickPrecision(n)))
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: prec,
        maximumFractionDigits: prec
      }).format(n)
    } catch (_) {
      return '$' + n.toFixed(prec)
    }
  }
  // Cache volume arrays and quick lookup maps for the tooltip
  const rebuildVolCache = arr => {
    try {
      if (!Array.isArray(arr)) arr = []
      const map = new Map()
      const idx = new Map()
      for (let i = 0; i < arr.length; i++) {
        const t = Number(arr[i]?.time)
        if (Number.isFinite(t)) {
          map.set(t, arr[i])
          idx.set(t, i)
        }
      }
      volDataRef.current = { arr, map }
      volIdxRef.current = idx
    } catch (_) {}
  }
  const rebuildHoldersCache = arr => {
    try {
      if (!Array.isArray(arr)) arr = []
      const map = new Map()
      const idx = new Map()
      for (let i = 0; i < arr.length; i++) {
        const t = Number(arr[i]?.time)
        if (Number.isFinite(t)) {
          map.set(t, arr[i])
          idx.set(t, i)
        }
      }
      holdersDataRef.current = { arr, map }
      holdersIdxRef.current = idx
    } catch (_) {}
  }

  // --- Helpers for trade markers (dev buy/sell) ---
  const findNearestIdx = (arr, ts) => {
    if (!Array.isArray(arr) || arr.length === 0) return -1
    // exact hit via map if available
    const direct = priceCacheRef.current.idx.get(ts)
    if (typeof direct === 'number') return direct
    // binary search by time
    let lo = 0,
      hi = arr.length - 1
    while (lo <= hi) {
      const mid = (lo + hi) >> 1
      const t = Number(arr[mid].time)
      if (t === ts) return mid
      if (t < ts) lo = mid + 1
      else hi = mid - 1
    }
    // lo is the first index greater than ts; choose nearest between hi and lo
    if (lo <= 0) return 0
    if (lo >= arr.length) return arr.length - 1
    const tLo = Math.abs(Number(arr[lo].time) - ts)
    const tHi = Math.abs(Number(arr[lo - 1].time) - ts)
    return tLo < tHi ? lo : lo - 1
  }

  const buildDevMarkers = () => {
    try {
      if (!Array.isArray(devEvents) || devEvents.length === 0) return []
      const arr = priceCacheRef.current.arr || []
      if (!arr.length) return []
      const markers = []
      for (let i = 0; i < devEvents.length; i++) {
        const e = devEvents[i]
        if (!e) continue
        const ts = Number(e.time)
        if (!Number.isFinite(ts)) continue
        const idx = findNearestIdx(arr, ts)
        if (idx < 0) continue
        const bar = arr[idx]
        const side = String(e.side || e.type || '').toLowerCase()
        const isBuy = side.includes('buy')
        const pos = isBuy ? 'belowBar' : 'aboveBar'
        const color = isBuy ? UI.bull : UI.bear
        // Short label with amount if available
        let txt = isBuy ? 'DEV BUY' : 'DEV SELL'
        const amt = Number(
          e.amount || e.amountBase || e.amountUsd || e.amountUSD
        )
        if (Number.isFinite(amt) && amt > 0) {
          const suffix = e.amountUsd != null || e.amountUSD != null ? '$' : ''
          const compact = n => {
            const a = Math.abs(n)
            if (a >= 1e9) return (n / 1e9).toFixed(1) + 'B'
            if (a >= 1e6) return (n / 1e6).toFixed(1) + 'M'
            if (a >= 1e3) return (n / 1e3).toFixed(1) + 'K'
            return n.toFixed(2)
          }
          try {
            txt += ` ${suffix}${compact(amt)}`
          } catch (_) {}
        }
        markers.push({
          time: Number(bar.time),
          position: pos,
          color,
          shape: isBuy ? 'arrowUp' : 'arrowDown',
          text: txt
        })
      }
      return markers
    } catch (_) {
      return []
    }
  }

  const applyDevMarkers = () => {
    try {
      const s = seriesRef.current
      if (!s || typeof s.setMarkers !== 'function') return
      const m = buildDevMarkers()
      s.setMarkers(m)
    } catch (_) {}
  }

  const isCandle = useMemo(() => !forceLine && hasOHLC(data), [data, forceLine])

  const candleData = useMemo(() => {
    if (!isCandle) return []
    const out = []
    for (let i = 0; i < data.length; i++) {
      const d = data[i]
      if (!d) continue
      const t = pickTime(d, i)
      const o = toNum(d.open)
      const c = toNum(d.close)
      if (![t, o, c].every(Number.isFinite)) continue
      let h = d.high != null ? toNum(d.high) : Math.max(o, c)
      let l = d.low != null ? toNum(d.low) : Math.min(o, c)
      if (![h, l].every(Number.isFinite)) {
        h = Math.max(o, c)
        l = Math.min(o, c)
      }
      out.push({
        time: t,
        open: o,
        high: h,
        low: l,
        close: c,
        volume: toNum(d.volume)
      })
    }
    out.sort((a, b) => a.time - b.time)
    return takeLast(dedupeCandlesSorted(out), MAX_POINTS)
  }, [data, isCandle])

  const lineData = useMemo(() => {
    if (isCandle) return []
    const out = []
    for (let i = 0; i < (Array.isArray(data) ? data.length : 0); i++) {
      const d = data[i]
      const ts = typeof d === 'object' ? d.time ?? d.timestamp : undefined
      const v = pickNumericValue(d, { valueKey })
      const t = pickTime({ time: ts }, i)
      if (!Number.isFinite(t) || v == null || !Number.isFinite(v)) continue
      out.push({ time: t, value: v })
    }
    out.sort((a, b) => a.time - b.time)
    const deduped = dedupeLineSorted(out)
    const aggregated = aggregateLineByMinute(deduped)
    return takeLast(aggregated, MAX_POINTS)
  }, [data, isCandle, valueKey])

  // Apply timeframe bucketing to computed series
  const bucketedCandleData = useMemo(
    () => (isCandle ? bucketCandles(candleData, timeframe) : []),
    [isCandle, candleData, timeframe]
  )
  const bucketedLineData = useMemo(
    () => (!isCandle ? bucketLine(lineData, timeframe) : []),
    [isCandle, lineData, timeframe]
  )

  useEffect(() => {
    /**
     * Chart lifecycle: Initialization and teardown logic runs only once on mount.
     * - Chart instance is created and configured on first mount.
     * - All refs and event listeners are cleaned up on unmount.
     * - onChartReady callback is kept up to date in a separate effect.
     * - Ensures no memory leaks or duplicate chart instances.
     * See above for the onChartReadyRef definition.
     */
    const el = containerRef.current
    if (!el) return

    let cancelled = false

    // If a previous chart instance exists (shouldn't in normal React flow), remove it
    if (chartRef.current) {
      try {
        chartRef.current.remove()
      } catch (_) {}
      chartRef.current = null
      seriesRef.current = null
    }

    const init = async () => {
      if (cancelled) return
      const width = Math.max(200, el.clientWidth || el.offsetWidth || 600)
      const height = Math.max(300, el.clientHeight || 500)

      let chart
      let source = 'unknown'
      // Attempt to load the Lightweight Charts library from local or CDN
      try {
        const res = await raceLoadLW()
        if (res?.mod?.createChart) {
          source = res.src
          chart = res.mod.createChart(el, {
            layout: {
              textColor: UI.text,
              background: { type: 'solid', color: UI.bg }
            },
            grid: {
              vertLines: { color: UI.grid },
              horzLines: { color: UI.grid }
            },
            crosshair: { mode: CrosshairMode.Normal },
            rightPriceScale: { borderColor: '#3a3a3c' },
            timeScale: {
              borderColor: '#3a3a3c',
              timeVisible: true,
              secondsVisible: true
            },
            width,
            height
          })
        }
      } catch (e) {
        // Only warn if chart creation fails entirely below.
      }
      // Fallback to any global LightweightCharts instance on window
      if (!chart) {
        try {
          const LWg =
            (typeof window !== 'undefined' &&
              (window.LightweightCharts || window?.LightweightCharts)) ||
            null
          if (LWg && typeof LWg.createChart === 'function') {
            chart = LWg.createChart(el, {
              layout: {
                textColor: UI.text,
                background: { type: 'solid', color: UI.bg }
              },
              grid: {
                vertLines: { color: UI.grid },
                horzLines: { color: UI.grid }
              },
              crosshair: { mode: CrosshairMode.Normal },
              rightPriceScale: { borderColor: '#3a3a3c' },
              timeScale: {
                borderColor: '#3a3a3c',
                timeVisible: true,
                secondsVisible: true
              },
              width,
              height
            })
          }
        } catch (_) {}
      }
      if (!chart) {
        console.warn(
          '[Chart] Unable to load Lightweight Charts module from any source.'
        )
        return
      }
      // Normalize chart instance in case vendor returns a wrapper object
      chart = unwrapChart(chart)
      // Slightly shift the time scale so newest values aren't glued to the right edge
      try {
        chart.applyOptions({
          rightPriceScale: { borderVisible: true },
          timeScale: { rightOffset: 2 }
        })
      } catch (_) {}
      // Subtle crosshair styling
      try {
        chart.applyOptions({
          crosshair: {
            mode: CrosshairMode.Magnet,
            vertLine: { width: 1, color: 'rgba(255,255,255,0.18)', style: 0 },
            horzLine: { width: 1, color: 'rgba(255,255,255,0.18)', style: 0 }
          }
        })
      } catch (_) {}
      // Notify parent that the chart is ready
      try {
        onChartReadyRef.current?.({ ready: true, source })
      } catch (_) {}

      chartRef.current = chart

      // Helper to get the main chart's right price scale width (for holders mini alignment)
      const getMainAxisWidth = () => {
        try {
          const ps = chart.priceScale ? chart.priceScale('right') : null
          const w = ps && typeof ps.width === 'function' ? ps.width() : 0
          return Number.isFinite(w) && w > 0 ? w : 0
        } catch (_) {
          return 0
        }
      }

      // --- Separate mini chart for Holders ---
      if (showHoldersMini && holdersPaneRef.current) {
        await createHoldersMini()
      }

      // Create a histogram series for volume overlaid at the bottom
      try {
        const vs = chart.addHistogramSeries({
          priceFormat: { type: 'volume' },
          priceScaleId: 'volume', // separate scale for volume
          color: VOL.neutral,
          lastValueVisible: false,
          priceLineVisible: false
        })
        try {
          vs.priceScale().applyOptions({
            // Make volume a compact strip pinned to the bottom of the main pane
            // top+bottom must be <= 1.0. This reserves ~18% for volume.
            scaleMargins: { top: 0.82, bottom: 0.0 },
            borderVisible: false,
            alignLabels: false,
            // hide its own axis completely (available on LW >=3)
            visible: false
          })
        } catch (_) {}
        volumeSeriesRef.current = vs
      } catch (e) {
        // Not critical: don't warn for volume series creation failure
        volumeSeriesRef.current = null
      }

      // Tooltip element in the container
      try {
        const tip = document.createElement('div')
        tip.style.position = 'absolute'
        tip.style.pointerEvents = 'none'
        tip.style.transform = 'translate(-50%, -120%)'
        tip.style.padding = '8px 10px'
        tip.style.borderRadius = '10px'
        tip.style.background = 'rgba(20, 20, 22, 0.92)'
        tip.style.border = '1px solid rgba(176, 67, 245, 0.55)'
        tip.style.boxShadow = '0 6px 16px rgba(0,0,0,0.35)'
        tip.style.backdropFilter = 'blur(2px)'
        tip.style.color = UI.text
        tip.style.fontSize = '12px'
        tip.style.fontWeight = '500'
        tip.style.lineHeight = '16px'
        tip.style.whiteSpace = 'nowrap'
        tip.style.zIndex = '999'
        tip.style.opacity = '0'
        tip.style.transition = 'opacity 120ms ease'
        tip.style.display = 'block'
        tip.style.pointerEvents = 'none'
        containerRef.current.appendChild(tip)
        tooltipRef.current = tip
      } catch (_) {}

      let created = null
      try {
        // First attempt: compat helper prefers native line, then area/baseline, then histogram
        created = addLineSeriesCompat(chart, { priceFormat: usdCompactFormat })
        if (!created && typeof chart.addLineSeries === 'function') {
          created = chart.addLineSeries({ priceFormat: usdCompactFormat })
        }
      } catch (e) {
        // swallow; we'll retry below
      }

      if (!created) {
        // Retry once on the next frame in case LW APIs attach a tick later
        await new Promise(r => requestAnimationFrame(r))
        try {
          created = addLineSeriesCompat(chart, {
            priceFormat: usdCompactFormat
          })
          if (!created && typeof chart.addLineSeries === 'function') {
            created = chart.addLineSeries({ priceFormat: usdCompactFormat })
          }
        } catch (_) {}
      }

      if (!created) {
        // Downgrade to debug to avoid noisy console when later effects succeed
        console.debug(
          '[Chart] Unable to create any series on init; will try again on data update.'
        )
      } else {
        seriesRef.current = created
        seriesKindRef.current = 'line'
      }
      firstFitDoneRef.current = false
      dataSigRef.current = ''

      try {
        seriesRef.current?.applyOptions({
          priceFormat: usdCompactFormat,
          lineWidth: 2,
          // Line series will use `color`; area series will honor lineColor/topColor/bottomColor
          color: UI.bull,
          lineColor: UI.bull,
          topColor: UI.areaTop,
          bottomColor: UI.areaBottom
        })
        try {
          chartRef.current.timeScale().fitContent()
        } catch (_) {}
      } catch (e) {
        console.warn('[Chart] applyOptions failed on series', e)
      }

      // Populate volume series if possible
      try {
        const vs = volumeSeriesRef.current
        if (vs) {
          if (isCandle && candleData.length) {
            const _vol = candleData.map(c => ({
              time: c.time,
              // Prefer USD notional if provided by BuyPage
              value:
                (c.volumeUsd != null
                  ? Number(c.volumeUsd)
                  : Number(c.volume ?? 0)) || 0,
              isUsd: c.volumeUsd != null,
              buyVolume: c.buyVolume,
              sellVolume: c.sellVolume,
              dir: c.close >= c.open ? 1 : -1
            }))
            vs.setData(
              _vol.map(v => ({
                time: v.time,
                value: v.value,
                color: v.dir > 0 ? VOL.up : VOL.down
              }))
            )
            rebuildVolCache(_vol)
          } else if (!isCandle && lineData.length) {
            const _vol = lineData.map(l => ({
              time: l.time,
              value:
                (l.volumeUsd != null
                  ? Number(l.volumeUsd)
                  : Number(l.volume ?? 0)) || 0,
              isUsd: l.volumeUsd != null,
              buyVolume: l.buyVolume,
              sellVolume: l.sellVolume
            }))
            vs.setData(
              _vol.map(v => ({
                time: v.time,
                value: v.value,
                color: VOL.neutral
              }))
            )
            rebuildVolCache(_vol)
          }
        }
      } catch (e) {
        // Not critical: don't warn for volume setData failure
      }

      // Subscribe to crosshair moves to update tooltip
      try {
        const fmtFull = n => {
          const v = Number(n)
          if (!Number.isFinite(v)) return '$0.00'
          return (
            '$' +
            v.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 6
            })
          )
        }
        const clamp = (v, min, max) => Math.max(min, Math.min(max, v))
        const onMove = param => {
          const tip = tooltipRef.current
          // Only require a valid time to show the tooltip; series/price can be missing on some builds
          if (!tip || !param || param.time == null) {
            if (tip) tip.style.opacity = '0'
            return
          }
          let price = undefined
          try {
            // Preferred: Map-like seriesPrices keyed by our series
            if (param.seriesPrices && seriesRef.current) {
              const sp = param.seriesPrices.get
                ? param.seriesPrices.get(seriesRef.current)
                : undefined
              if (sp != null) price = sp
            }
            // Fallback #1: first value from seriesPrices iterator (some builds)
            if (
              price == null &&
              param.seriesPrices &&
              typeof param.seriesPrices.values === 'function'
            ) {
              const it = param.seriesPrices.values()
              const first = it && it.next ? it.next() : null
              if (first && !first.done) price = first.value
            }
            // Fallback #2: seriesData array (v3 style)
            if (
              price == null &&
              Array.isArray(param.seriesData) &&
              param.seriesData.length
            ) {
              const d0 = param.seriesData[0]
              price = d0.value ?? d0.close ?? d0.open ?? d0.high ?? d0.low
            }
          } catch (_) {}
          // Do not early-return if price is null; we can still show time + volume

          const ts = Number(param.time)
          const d = new Date(ts * 1000)
          const hh = String(d.getHours()).padStart(2, '0')
          const mm = String(d.getMinutes()).padStart(2, '0')
          const ss = String(d.getSeconds()).padStart(2, '0')

          const fmtCompact = n => {
            const v = Number(n)
            if (!Number.isFinite(v) || v === 0) return '0'
            const a = Math.abs(v)
            if (a >= 1e9) return (v / 1e9).toFixed(2) + 'B'
            if (a >= 1e6) return (v / 1e6).toFixed(2) + 'M'
            if (a >= 1e3) return (v / 1e3).toFixed(2) + 'K'
            return v.toFixed(2)
          }

          // Volume lookup at this time (from latest cache)
          let volStr = ''
          try {
            const volNow = volDataRef.current.map.get(ts)
            if (volNow) {
              const total = Number(volNow.value) || 0
              const isUsd = !!volNow.isUsd
              // previous bar change (volume)
              let delta = ''
              try {
                const idx = volIdxRef.current.get(ts)
                if (typeof idx === 'number' && idx > 0) {
                  const prev =
                    Number(volDataRef.current.arr[idx - 1]?.value) || 0
                  if (prev > 0 && total >= 0) {
                    const ch = ((total - prev) / prev) * 100
                    const sign = ch >= 0 ? '+' : ''
                    const color = ch >= 0 ? UI.bull : UI.bear
                    delta = ` <span style="color:${color}">(${sign}${ch.toFixed(
                      1
                    )}%)</span>`
                  }
                }
              } catch (_) {}
              const fmt = v => (isUsd ? '$' + fmtCompact(v) : fmtCompact(v))
              volStr = ` · Vol ${fmt(total)}${delta}`
            }
          } catch (_) {}

          // Holders lookup at this time
          let holdersStr = ''
          try {
            const hNow = holdersDataRef.current.map.get(ts)
            if (hNow && Number.isFinite(Number(hNow.value))) {
              const hv = Number(hNow.value)
              holdersStr = ` · Holders: ${hv.toLocaleString()}`
            }
          } catch (_) {}

          // Compact price change vs previous bar/point
          let changeStr = ''
          try {
            const idx = priceCacheRef.current.idx.get(ts)
            if (typeof idx === 'number') {
              const cur = priceCacheRef.current.arr[idx]
              const prev = idx > 0 ? priceCacheRef.current.arr[idx - 1] : null
              let curVal = null,
                prevVal = null
              if (isCandle && cur) {
                curVal = Number(cur.close)
                prevVal = prev ? Number(prev.close) : null
              } else if (cur) {
                curVal = Number(cur.value)
                prevVal = prev ? Number(prev.value) : null
              }
              if (Number.isFinite(curVal)) {
                if (price == null) price = curVal
                if (Number.isFinite(prevVal) && prevVal !== 0) {
                  const ch = ((curVal - prevVal) / prevVal) * 100
                  const sign = ch >= 0 ? '+' : ''
                  const color = ch >= 0 ? UI.bull : UI.bear
                  changeStr = ` · <span style="color:${color}">${sign}${ch.toFixed(
                    2
                  )}%</span>`
                }
              }
            }
          } catch (_) {}

          const pricePart = price == null ? '' : ` · ${fmtFull(price)}`
          tip.innerHTML = `<span style="font-weight:600">${hh}:${mm}:${ss}</span>${pricePart}${volStr}${holdersStr}${changeStr}`

          const bounds = containerRef.current.getBoundingClientRect()
          const px = clamp(param.point?.x ?? 0, 8, bounds.width - 8)
          const py = clamp(param.point?.y ?? 0, 16, bounds.height - 16)
          tip.style.left = px + 'px'
          // Flip placement if near the top edge
          const above = py > bounds.height * 0.25
          tip.style.transform = above
            ? 'translate(-50%, -120%)'
            : 'translate(-50%, 12px)'
          tip.style.top = py + 'px'
          tip.style.opacity = '1'
          if (!param.point) {
            tip.style.opacity = '0'
          }
        }
        chart.subscribeCrosshairMove(onMove)
        // Hide tooltip when mouse leaves the container
        const onLeave = () => {
          if (tooltipRef.current) tooltipRef.current.style.opacity = '0'
        }
        try {
          containerRef.current?.addEventListener('mouseleave', onLeave)
        } catch (_) {}
        // Extend unsubscribe to also remove the mouseleave listener
        const prevUnsub = unsubscribeRef.current
        unsubscribeRef.current = () => {
          try {
            chart.unsubscribeCrosshairMove(onMove)
          } catch (_) {}
          try {
            containerRef.current?.removeEventListener('mouseleave', onLeave)
          } catch (_) {}
          if (typeof prevUnsub === 'function') {
            try {
              prevUnsub()
            } catch (_) {}
          }
        }
      } catch (_) {}

      // Resize the chart whenever the container changes size. We debounce
      // updates using requestAnimationFrame to avoid thrashing.
      const ro = new ResizeObserver(entries => {
        const entry = entries[0]
        if (!entry || !chartRef.current) return
        if (resizeRAFIdRef.current) cancelAnimationFrame(resizeRAFIdRef.current)
        resizeRAFIdRef.current = requestAnimationFrame(() => {
          const { width: w, height: h } = entry.contentRect
          chartRef.current.applyOptions({
            width: Math.max(200, Math.floor(w)),
            height: Math.max(300, Math.floor(h))
          })
          // keep holders chart sized too and aligned
          alignHoldersAxis()
          // On the first resize after data exists, auto-fit once
          try {
            const hasData =
              Array.isArray(isCandle ? candleData : lineData) &&
              (isCandle ? candleData.length : lineData.length)
            if (
              autoFitOnFirstData &&
              hasData &&
              !firstResizeFitDoneRef.current
            ) {
              firstResizeFitDoneRef.current = true
              chartRef.current.timeScale().fitContent()
            }
          } catch (_) {}
        })
      })
      ro.observe(el)
      resizeObserverRef.current = ro
    }

    const raf = requestAnimationFrame(() => {
      init()
    })

    return () => {
      cancelled = true
      // Cancel any scheduled initialization
      try {
        cancelAnimationFrame(raf)
      } catch (_) {}
      // Cancel any pending resize animation frame
      try {
        if (resizeRAFIdRef.current) cancelAnimationFrame(resizeRAFIdRef.current)
      } catch (_) {}
      resizeRAFIdRef.current = 0
      // Disconnect the ResizeObserver
      try {
        resizeObserverRef.current?.disconnect()
      } catch (_) {}
      // Notify parent that the chart has unmounted
      try {
        onChartReadyRef.current?.({ ready: false })
      } catch (_) {}
      // Remove the chart instance
      try {
        chartRef.current?.remove()
      } catch (_) {}
      // Tooltip cleanup
      try {
        unsubscribeRef.current?.()
      } catch (_) {}
      try {
        if (tooltipRef.current) tooltipRef.current.remove()
      } catch (_) {}
      tooltipRef.current = null
      unsubscribeRef.current = null
      // Remove volume series if present
      try {
        if (volumeSeriesRef.current && chartRef.current)
          chartRef.current.removeSeries?.(volumeSeriesRef.current)
      } catch (_) {}
      volumeSeriesRef.current = null
      // Remove holders mini chart
      destroyHoldersMini()
      // Remove holders series if present
      try {
        if (holdersSeriesRef.current && chartRef.current)
          chartRef.current.removeSeries?.(holdersSeriesRef.current)
      } catch (_) {}
      holdersSeriesRef.current = null
      // Clear refs
      seriesRef.current = null
      chartRef.current = null
      resizeObserverRef.current = null
    }
  }, [])

  // Effect to mount/destroy holders mini chart on arrow toggle and realign axes
  useEffect(() => {
    const c = chartRef.current
    if (!holdersPaneRef.current || !c) return
    ;(async () => {
      if (showHoldersMini) {
        if (!holdersChartRef.current) await createHoldersMini()
        alignHoldersAxis()
        requestAnimationFrame(() => alignHoldersAxis())
        try {
          requestAnimationFrame(() => c.timeScale().fitContent())
        } catch (_) {}
        setTimeout(() => alignHoldersAxis(), 150)
      } else {
        destroyHoldersMini()
        try {
          requestAnimationFrame(() => c.timeScale().fitContent())
        } catch (_) {}
      }
    })()
  }, [showHoldersMini])

  useEffect(() => {
    const chart = chartRef.current
    let series = seriesRef.current
    if (!chart || !series) return

    // Data → series pipeline: Process bucketed data, update series, axis, caches, and markers for the current chart mode and timeframe.

    // Build a tiny change signature: kind|len|firstTs|lastTs|lastVal/close
    const arr = isCandle ? bucketedCandleData : bucketedLineData
    const len = Array.isArray(arr) ? arr.length : 0
    const firstTs = len ? Number(arr[0].time) : 0
    const lastTs = len ? Number(arr[len - 1].time) : 0
    const lastVal = len
      ? isCandle
        ? Number(arr[len - 1].close)
        : Number(arr[len - 1].value)
      : 0
    // Dynamic precision picking
    const typicalPx = len
      ? isCandle
        ? Number(arr[len - 1].close)
        : Number(arr[len - 1].value)
      : 0
    const prec = pickPrecision(typicalPx)
    const minMove = Math.pow(10, -prec)
    const dynamicPriceFormat = { type: 'price', precision: prec, minMove }
    const nextSig = `${
      isCandle ? 'c' : 'l'
    }|${len}|${firstTs}|${lastTs}|${lastVal}`
    if (nextSig === dataSigRef.current) {
      return // no meaningful change; skip expensive setData
    }

    try {
      if (isCandle && bucketedCandleData.length) {
        if (seriesKindRef.current !== 'candle') {
          try {
            chart.removeSeries(series)
          } catch (_) {}
          try {
            series = addCandleSeriesCompat(chart, {
              priceFormat: dynamicPriceFormat
            })
            // Stylized candlesticks
            try {
              series.applyOptions({
                upColor: UI.bull,
                borderUpColor: UI.bull,
                wickUpColor: UI.bull,
                downColor: UI.bear,
                borderDownColor: UI.bear,
                wickDownColor: UI.bear
              })
            } catch (_) {}
            seriesRef.current = series
            seriesKindRef.current = 'candle'
          } catch (e) {
            // Only warn if candlestick creation fails and is a real issue
            console.warn(
              '[Chart] addSeries(Candlestick) failed. Keeping line series.',
              e
            )
            series = seriesRef.current
            seriesKindRef.current = 'line'
          }
        }
        if (!series) return
        series.applyOptions &&
          series.applyOptions({ priceFormat: dynamicPriceFormat })
        // Chart-level price formatter for axis
        try {
          chart.applyOptions({
            localization: {
              priceFormatter: axisUsdFormatter
            }
          })
        } catch (_) {}
        series.setData(
          Array.isArray(bucketedCandleData) ? bucketedCandleData : []
        )
        if (autoFitOnFirstData && !firstFitDoneRef.current) {
          firstFitDoneRef.current = true
          try {
            chart.timeScale().fitContent()
          } catch (_) {}
        }
        // Extra safety: refit again on the next frame and after a short delay
        // to catch late font/layout shifts or parent flex changes.
        if (autoFitOnFirstData) {
          try {
            requestAnimationFrame(() => {
              try {
                chart.timeScale().fitContent()
              } catch (_) {}
            })
            setTimeout(() => {
              try {
                chart.timeScale().fitContent()
              } catch (_) {}
            }, 200)
          } catch (_) {}
        }
        // Build price cache for tooltip (candles)
        try {
          const arrCache = bucketedCandleData.map(c => ({
            time: c.time,
            open: c.open,
            high: c.high,
            low: c.low,
            close: c.close
          }))
          const mapIdx = new Map()
          for (let i = 0; i < arrCache.length; i++)
            mapIdx.set(arrCache[i].time, i)
          priceCacheRef.current = { arr: arrCache, idx: mapIdx }
        } catch (_) {}
        // Apply dev markers after cache is built
        try {
          requestAnimationFrame(applyDevMarkers)
        } catch (_) {}
        dataSigRef.current = nextSig
        // Update (or create) a highlighted ATH price line
        try {
          // Determine ATH from all points in current array
          const lp = isCandle
            ? Math.max(...arr.map(p => Number(p.close)))
            : Math.max(...arr.map(p => Number(p.value)))
          if (Number.isFinite(lp) && lp !== lastPriceRef.current) {
            if (priceLineRef.current) {
              try {
                series.removePriceLine(priceLineRef.current)
              } catch (_) {}
            }
            priceLineRef.current = series.createPriceLine({
              price: lp,
              color: UI.bull,
              lineWidth: 2,
              lineStyle: 2,
              axisLabelVisible: true,
              title: 'ATH'
            })
            lastPriceRef.current = lp
          }
        } catch (_) {}
      } else {
        if (seriesKindRef.current !== 'line') {
          try {
            chart.removeSeries(series)
          } catch (_) {}
          let created = addLineSeriesCompat(chart, {
            priceFormat: dynamicPriceFormat
          })
          if (
            !created &&
            window?.LightweightCharts &&
            typeof chart?.addLineSeries === 'function'
          ) {
            created = chart.addLineSeries({ priceFormat: dynamicPriceFormat })
          }
          if (!created) {
            // Only warn if no line series could be created at all
            console.warn(
              '[Chart] Unable to create line series on update; skipping setData.'
            )
            return
          }
          series = created
          seriesRef.current = created
          seriesKindRef.current = 'line'
          // Upgraded line/area style
          try {
            series.applyOptions({
              priceFormat: dynamicPriceFormat,
              lineWidth: 2,
              color: UI.bull,
              lineColor: UI.bull,
              topColor: UI.areaTop,
              bottomColor: UI.areaBottom
            })
          } catch (_) {}
        }
        if (!series) return
        series.applyOptions &&
          series.applyOptions({ priceFormat: dynamicPriceFormat })
        // Chart-level price formatter for axis
        try {
          chart.applyOptions({
            localization: {
              priceFormatter: axisUsdFormatter
            }
          })
        } catch (_) {}
        series.setData(Array.isArray(bucketedLineData) ? bucketedLineData : [])
        if (autoFitOnFirstData && !firstFitDoneRef.current) {
          firstFitDoneRef.current = true
          try {
            chart.timeScale().fitContent()
          } catch (_) {}
        }
        // Extra safety: refit again on the next frame and after a short delay
        // to catch late font/layout shifts or parent flex changes.
        if (autoFitOnFirstData) {
          try {
            requestAnimationFrame(() => {
              try {
                chart.timeScale().fitContent()
              } catch (_) {}
            })
            setTimeout(() => {
              try {
                chart.timeScale().fitContent()
              } catch (_) {}
            }, 200)
          } catch (_) {}
        }
        // Build price cache for tooltip (line)
        try {
          const arrCache = bucketedLineData.map(p => ({
            time: p.time,
            value: p.value
          }))
          const mapIdx = new Map()
          for (let i = 0; i < arrCache.length; i++)
            mapIdx.set(arrCache[i].time, i)
          priceCacheRef.current = { arr: arrCache, idx: mapIdx }
        } catch (_) {}
        // Apply dev markers after cache is built
        try {
          requestAnimationFrame(applyDevMarkers)
        } catch (_) {}
        dataSigRef.current = nextSig
        // Update (or create) a highlighted ATH price line
        try {
          // Determine ATH from all points in current array
          const lp = isCandle
            ? Math.max(...arr.map(p => Number(p.close)))
            : Math.max(...arr.map(p => Number(p.value)))
          if (Number.isFinite(lp) && lp !== lastPriceRef.current) {
            if (priceLineRef.current) {
              try {
                series.removePriceLine(priceLineRef.current)
              } catch (_) {}
            }
            priceLineRef.current = series.createPriceLine({
              price: lp,
              color: UI.bull,
              lineWidth: 2,
              lineStyle: 2,
              axisLabelVisible: true,
              title: 'ATH'
            })
            lastPriceRef.current = lp
          }
        } catch (_) {}
      }
      // Update volume series using the ref
      try {
        const vs = volumeSeriesRef.current
        if (vs) {
          if (isCandle && bucketedCandleData.length) {
            const _vol = bucketedCandleData.map(c => ({
              time: c.time,
              // Prefer USD notional if provided by BuyPage
              value:
                (c.volumeUsd != null
                  ? Number(c.volumeUsd)
                  : Number(c.volume ?? 0)) || 0,
              isUsd: c.volumeUsd != null,
              buyVolume: c.buyVolume,
              sellVolume: c.sellVolume,
              dir: c.close >= c.open ? 1 : -1
            }))
            vs.setData(
              _vol.map(v => ({
                time: v.time,
                value: v.value,
                color: v.dir > 0 ? VOL.up : VOL.down
              }))
            )
            rebuildVolCache(_vol)
          } else if (!isCandle && bucketedLineData.length) {
            const _vol = bucketedLineData.map(l => ({
              time: l.time,
              value:
                (l.volumeUsd != null
                  ? Number(l.volumeUsd)
                  : Number(l.volume ?? 0)) || 0,
              isUsd: l.volumeUsd != null,
              buyVolume: l.buyVolume,
              sellVolume: l.sellVolume
            }))
            vs.setData(
              _vol.map(v => ({
                time: v.time,
                value: v.value,
                color: VOL.neutral
              }))
            )
            rebuildVolCache(_vol)
          } else {
            vs.setData([])
            rebuildVolCache([])
          }
        }
      } catch (e) {
        // Not critical: don't warn for volume update failure
      }
      // Update holders mini‑pane (separate chart)
      try {
        const ha = holdersAreaRef.current
        if (showHoldersMini && ha) {
          if (Array.isArray(holdersTrend) && holdersTrend.length) {
            const _h = holdersTrend
              .map(d => ({ time: Number(d.time), value: Number(d.value) }))
              .filter(p => Number.isFinite(p.time) && Number.isFinite(p.value))
              .sort((a, b) => a.time - b.time)
            ha.setData(_h)
            rebuildHoldersCache(_h)
          } else {
            ha.setData([])
            rebuildHoldersCache([])
          }
        }
      } catch (_) {}
    } catch (e) {
      // Only warn if setData fails entirely (critical)
      console.warn('[Chart] setData failed, clearing series', e)
      try {
        series.setData([])
      } catch (_) {}
    }
  }, [isCandle, bucketedCandleData, bucketedLineData])

  useEffect(() => {
    // Re-apply markers when devEvents change
    applyDevMarkers()
  }, [devEvents])

  // Refit on mode/valueKey/height/timeframe changes (e.g., toggling MC ↔ Price or resizing)
  useEffect(() => {
    const c = chartRef.current
    if (!c) return
    try {
      requestAnimationFrame(() => {
        try {
          c.timeScale().fitContent()
        } catch (_) {}
        alignHoldersAxis()
      })
    } catch (_) {}
  }, [mode, valueKey, height, timeframe])

  // Force auto-fit when first non-empty data arrives AND container is ready
  useEffect(() => {
    const c = chartRef.current
    const el = containerRef.current
    if (!c || !el) return
    const len = Array.isArray(isCandle ? candleData : lineData)
      ? isCandle
        ? candleData.length
        : lineData.length
      : 0
    const hasSize = el.clientWidth > 0 && el.clientHeight > 0
    if (autoFitOnFirstData && len > 0 && hasSize && !firstFitDoneRef.current) {
      firstFitDoneRef.current = true
      // Fit now
      try {
        c.timeScale().fitContent()
      } catch (_) {}
      // Fit again after one frame
      requestAnimationFrame(() => {
        try {
          c.timeScale().fitContent()
        } catch (_) {}
      })
      // Fit again after delay to catch late layout shifts
      setTimeout(() => {
        try {
          c.timeScale().fitContent()
        } catch (_) {}
      }, 500)
    }
  }, [isCandle, candleData, lineData, autoFitOnFirstData])

  return (
    <div className="w-full">
      <div
        className="rounded-xl border border-[#3a3a3c] bg-[#171a21] overflow-hidden"
        style={{
          height: typeof height === 'number' ? height : height || 500,
          width: '100%',
          position: 'relative'
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'grid',
            gridTemplateRows: '1fr var(--holders-h, 0px)',
            position: 'relative',
            '--holders-h': showHoldersMini ? '96px' : '0px'
          }}
        >
          <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
          <div
            ref={holdersPaneRef}
            style={{
              width: '100%',
              height: 'var(--holders-h, 0px)',
              overflow: 'hidden'
            }}
          />
        </div>
        {overlayControls && (
          /* Overlay controls: timeframe switcher, MC/Price toggle, compact legend */
          <div
            className="hc-overlay"
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            {/* Timeframe Switcher */}
            <div
              className="hc-tf"
              style={{
                display: 'inline-flex',
                overflow: 'hidden',
                borderRadius: 8,
                border: '1px solid #3a3a3c',
                background: '#111',
                pointerEvents: 'auto'
              }}
              title="Timeframe"
            >
              {[
                { label: '1m', val: 1 },
                { label: '5m', val: 5 },
                { label: '15m', val: 15 },
                { label: '1h', val: 60 },
                { label: '1d', val: 1440 }
              ].map(tf => (
                <button
                  key={tf.val}
                  type="button"
                  onClick={() => setTimeframe(tf.val)}
                  aria-pressed={timeframe === tf.val}
                  style={{
                    padding: '6px 8px',
                    fontSize: 12,
                    fontWeight: 700,
                    color: timeframe === tf.val ? '#fff' : '#cfcfcf',
                    background:
                      timeframe === tf.val ? 'transparent' : '#2a2a2e',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  {tf.label}
                </button>
              ))}
            </div>
            <div
              className="hc-toggle"
              style={{
                display: 'inline-flex',
                overflow: 'hidden',
                borderRadius: 8,
                border: '1px solid #3a3a3c',
                background: '#111'
              }}
            >
              <button
                type="button"
                onClick={() => onModeChange && onModeChange('mcap')}
                style={{
                  padding: '6px 10px',
                  fontSize: 12,
                  fontWeight: 700,
                  color: mode === 'mcap' ? '#fff' : '#cfcfcf',
                  background: mode === 'mcap' ? 'transparent' : '#2a2a2e',
                  border: 'none',
                  cursor: 'pointer'
                }}
                aria-pressed={mode === 'mcap'}
              >
                MC
              </button>
              <button
                type="button"
                onClick={() => onModeChange && onModeChange('price')}
                style={{
                  padding: '6px 10px',
                  fontSize: 12,
                  fontWeight: 700,
                  color: mode === 'price' ? '#fff' : '#cfcfcf',
                  background: mode === 'price' ? 'transparent' : '#2a2a2e',
                  border: 'none',
                  cursor: 'pointer'
                }}
                aria-pressed={mode === 'price'}
              >
                Price
              </button>
            </div>
            <div
              className="hc-legend"
              style={{
                padding: '4px 8px',
                borderRadius: 8,
                background: 'rgba(17,17,17,0.7)',
                border: '1px solid #3a3a3c',
                color: '#fff',
                fontSize: 12,
                lineHeight: '16px',
                backdropFilter: 'blur(4px)'
              }}
            >
              {valueKey ? 'Price: ' : 'MC: '}
              <strong>{fmtUSDcompact(lastValue)}</strong>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

MyChart.propTypes = {
  data: PropTypes.array.isRequired,
  ethPrice: PropTypes.number,
  valueKey: PropTypes.string,
  forceLine: PropTypes.bool,
  onChartReady: PropTypes.func,
  overlayControls: PropTypes.bool,
  mode: PropTypes.oneOf(['mcap', 'price']),
  onModeChange: PropTypes.func,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  autoFitOnFirstData: PropTypes.bool,
  holdersTrend: PropTypes.array,
  showHoldersMini: PropTypes.bool
}

export default MyChart
